import {
  SOURCE_AMBIENT_WEATHER,
  SOURCE_DAVIS,
  SOURCE_ECOWITT,
  SOURCE_MY_ACURITE,
  SOURCE_NETATMO_WEATHER,
  SOURCE_SYNOP,
  SOURCE_WEATHERFLOW,
  SOURCE_WUNDERGROUND,
} from '../../api';
import {
  ProviderStationLink,
  StationMeta,
  StationSource,
  WeatherStationFromList,
} from '../../api/types';
import {DateTimeConfig, formatDate, timezoneDateFromTimestamp} from '../Dates';
import {clockFormat} from '../Dates/clock';

export const getCustomStationName = (s: WeatherStationFromList) =>
  (s.link && s.link?.user_data?.name) || s.custom_name || '';

export const getStationName = (s: WeatherStationFromList) => {
  if (s) {
    return (
      (s.link && (s.link?.user_data?.name || s.link.name)) ||
      s.custom_name ||
      s.name
    );
  }
  return '';
};

/**
 * Whether the owner has hidden this linked station from their lists.
 *
 * Only linked stations can be hidden, and only the owner's `user_data.hidden`
 * says so. Reads through `?.` on purpose: `user_data` is absent entirely on
 * links rehydrated from a persisted store written before the backend grew the
 * field (SMARTMIXIN-IOS-6R), and an absent key means "no user overrides", i.e.
 * not hidden. Every hidden/visible split goes through here so the guard cannot
 * drift back out of one of the call sites.
 */
export const isStationHidden = (s: WeatherStationFromList) =>
  Boolean(s.link && s.link.user_data?.hidden);

/**
 * Whether we positively know this station's provider account is disconnected.
 *
 * Deliberately `=== false` rather than `!account_active`: the flag is absent on
 * links rehydrated from a store predating it, and null for a station with no
 * provider account, and neither of those means "inactive" — a truthiness test
 * showed a disconnected-account banner on stations that were perfectly fine.
 * Same rehydration hazard as `isStationHidden` (SMARTMIXIN-IOS-6R).
 */
export const isProviderAccountInactive = (s: WeatherStationFromList) =>
  s?.link?.account_active === false;

/**
 * Epoch seconds the station dropped out of its provider account listing, or
 * null while it is listed (or not a provider station at all).
 *
 * Since backend !684 such a station is kept, with its history, instead of
 * deleted — usually the old device after a hardware swap — but it receives no
 * new data. The key is absent on links rehydrated from a store predating it,
 * and absent means live (same hazard as `isStationHidden`, SMARTMIXIN-IOS-6R).
 */
export const getMissingFromListingSince = (s: {
  link?: ProviderStationLink | null;
}): number | null => s?.link?.missing_from_listing_since ?? null;

/** Whether the station left its provider listing and no longer reports. */
export const isStationRetired = (s: {link?: ProviderStationLink | null}) =>
  getMissingFromListingSince(s) !== null;

const SOURCE_NAMES: Record<StationSource, string> = {
  [SOURCE_SYNOP]: 'Synop/Metar',
  [SOURCE_NETATMO_WEATHER]: 'Netatmo',
  [SOURCE_DAVIS]: 'Davis Instruments',
  [SOURCE_WEATHERFLOW]: 'Weatherflow Tempest',
  [SOURCE_AMBIENT_WEATHER]: 'Ambient Weather',
  [SOURCE_WUNDERGROUND]: 'Weather Underground',
  [SOURCE_ECOWITT]: 'Ecowitt',
  [SOURCE_MY_ACURITE]: 'My AcuRite',
};

/** The data source's brand name, untranslated; null for an unknown source. */
export const getSourceName = (source: StationSource): string | null =>
  SOURCE_NAMES[source] ?? null;

export const isWeatherflowRainCheckEnabled = (meta: StationMeta | undefined) =>
  meta?.weatherflow_raincheck_enabled !== false;

/**
 * Whether the station's hourly history buckets are true aggregations of
 * sub-hourly samples. All provider stations report every few minutes, so
 * the backend computes per-hour min/max for them; SYNOP hourly rows are
 * single point observations.
 */
export const hasAggregatedHourlyHistory = (s: WeatherStationFromList) =>
  s.source !== SOURCE_SYNOP;

export const getRainToday = (s: WeatherStationFromList) => {
  if (s) {
    if (
      s.source === SOURCE_WEATHERFLOW &&
      isWeatherflowRainCheckEnabled(s.meta) &&
      !isNaN(Number(s.last_measure?.RR_TODAY_RAIN_CHECK))
    ) {
      // Weatherlink Rain Check
      return s.last_measure?.RR_TODAY_RAIN_CHECK;
    }
    if (!isNaN(Number(s.last_measure?.RR_TODAY))) {
      return s.last_measure?.RR_TODAY;
    }
    if (!isNaN(Number(s.last_measure?.RR_PIEZO_TODAY))) {
      return s.last_measure?.RR_PIEZO_TODAY;
    }
  }
  return null;
};

/**
 * How old a rolling 24h rain total may be and still be shown. RR_24H is
 * usually reported once a day, so the latest window often ended hours ago;
 * past this, the station has stopped sending it (prod 2026-10-26: 54 of 384
 * such stations, up to 47 days old) and the value says nothing about now.
 */
export const ROLLING_RAIN_24H_MAX_AGE_S = 30 * 60 * 60;

/**
 * The rolling 24h rain of a station with no calendar-day total (SYNOP
 * stations reporting only RR_24H, #78), with the time its window ended on the
 * station's clock and whether that was yesterday there, or null when the
 * station has a calendar-day total, the value or its window end is missing,
 * or the window ended too long ago. The backend keeps the last window past
 * local midnight until the next report (prod 2026-10-27 01:00 in Manila:
 * yesterday's 08:00), so "yesterday" is common; two days back is hidden.
 */
export const getRollingRain24h = (
  s: WeatherStationFromList,
  dateTimeConfig?: DateTimeConfig,
  nowS: number = Date.now() / 1000,
): {value: number; time: string; yesterday: boolean} | null => {
  const value = s?.last_measure?.RR_LAST_24H;
  const endsAt = s?.last_measure?.RR_LAST_24H_AT;
  if (
    typeof value !== 'number' ||
    typeof endsAt !== 'number' ||
    getRainToday(s) !== null ||
    nowS - endsAt > ROLLING_RAIN_24H_MAX_AGE_S
  ) {
    return null;
  }
  const daysAgo = timezoneDateFromTimestamp(nowS, s.timezone)
    .startOf('day')
    .diff(timezoneDateFromTimestamp(endsAt, s.timezone).startOf('day'), 'days');
  if (daysAgo > 1) {
    return null;
  }
  return {
    value,
    time: formatDate(endsAt, s.timezone, clockFormat(dateTimeConfig)),
    yesterday: daysAgo === 1,
  };
};

export const getRainYesterday = (s: WeatherStationFromList) => {
  if (s) {
    if (
      s.source === SOURCE_WEATHERFLOW &&
      isWeatherflowRainCheckEnabled(s.meta) &&
      !isNaN(Number(s.last_measure?.RR_YESTERDAY_RAIN_CHECK))
    ) {
      // Weatherlink Rain Check
      return s.last_measure?.RR_YESTERDAY_RAIN_CHECK;
    }
    if (!isNaN(Number(s.last_measure?.RR_YESTERDAY))) {
      return s.last_measure?.RR_YESTERDAY;
    }
  }
  return null;
};
